import React from 'react';
import {
  Control,
  FieldValues,
  FormState,
  UseFormGetValues,
  UseFormSetValue,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import TextInput from '../../form/TextInput';
import Name from '../../form/Name';
import Address from '../../form/Address';
import SelectMenu from '../../form/SelectMenu';
import DateInput from '../../form/DateInput';
import { StateCode } from '../../../data/models/state-code.model';
import { LegalEntityType } from '../../../data/models/legal-entity-type.model';
import { LegalEntityStatus } from '../../../data/models/legal-entity-status.model';
import PhoneInput from '../../form/PhoneInput';
import validationUtils from '../../../services/validationUtils';
import { Box, Button } from '@mui/material';
import addressUtils from '../../../services/addressUtils';
import { formClasses } from '../ElementDialog';
import { DateTime } from 'luxon';

interface LegalInfoFormProps {
  control: Control;
  formState: FormState<any>;
  requiredFieldMessage: string;
  stateCodes: StateCode[];
  legalEntityTypes: LegalEntityType[];
  legalEntityStatuses: LegalEntityStatus[];
  getValues: UseFormGetValues<FieldValues>;
  setValue: UseFormSetValue<any>;
}

export default function LegalInfoForm({
  control,
  formState,
  requiredFieldMessage,
  stateCodes,
  legalEntityTypes,
  legalEntityStatuses,
  getValues,
  setValue,
}: LegalInfoFormProps) {
  const { t } = useTranslation('common');

  const [minDate, setMinDate] = React.useState<DateTime>();
  const [maxDate, setMaxDate] = React.useState<DateTime>();
  React.useEffect(() => {
    setMinDate(DateTime.now().minus({ years: 5 }).startOf('year'));
    setMaxDate(DateTime.now().plus({ years: 15 }).endOf('year'));
  }, []);

  const copyDispatchAddress = (evt: any) => {
    evt.preventDefault();
    addressUtils.copyDispatchToPhysical(getValues(), setValue);
  };

  const copyBillingAddress = (evt: any) => {
    evt.preventDefault();
    addressUtils.copyBillingToPhysical(getValues(), setValue);
  };

  return (
    <>
      <Name
        namePrefix="Owner"
        control={control}
        formState={formState}
        required={false}
        firstNameLabel={t('provider.ownerFirstName') || ''}
        lastNameLabel={t('provider.ownerLastName') || ''}
      />
      <TextInput
        label={t('provider.ownerEmail')}
        name="OwnerEmailAddress"
        required={false}
        control={control}
        errors={formState.errors}
        rules={{
          pattern: validationUtils.emailValidation(),
        }}
        inputProps={{ maxLength: 255 }}
      />
      <PhoneInput
        label={t('provider.ownerPhone')}
        name="OwnerPhone"
        extensionName="OwnerPhoneExtension"
        required={false}
        control={control}
        errors={formState.errors}
      />
      <TextInput
        label={t('provider.ein')}
        name="EmployerIdentificationNumber"
        required={false}
        control={control}
        errors={formState.errors}
        inputProps={{ maxLength: 10 }}
      />
      <SelectMenu
        label={t('provider.bizState')}
        name="LegalEntityStateCode"
        values={stateCodes}
        valueAttr="stateCode"
        labelAttr="stateName"
        required={false}
        control={control}
        errors={formState.errors}
      />
      <Box className={formClasses.copyButtons}>
        <Button
          href="#"
          variant="contained"
          color="secondary"
          onClick={copyDispatchAddress}>
          {t('provider.copyDispatchAddress')}
        </Button>
        <Button
          href="#"
          variant="contained"
          color="secondary"
          onClick={copyBillingAddress}>
          {t('provider.copyBillingAddress')}
        </Button>
      </Box>
      <Address
        namePrefix="Physical"
        control={control}
        formState={formState}
        stateCodes={stateCodes}
        lineOneLabel={t('provider.physicalAddressLine1') || ''}
        requiredFieldMessage={requiredFieldMessage}
      />
      <DateInput
        label={t('provider.permitExpires')}
        name="BusinessPermitExpiresOn"
        control={control}
        required={false}
        errors={formState.errors}
        minDate={minDate}
        maxDate={maxDate}
      />
      <SelectMenu
        label={t('provider.legalEntityType')}
        name="LegalEntityTypeID"
        values={legalEntityTypes}
        valueAttr="legalEntityTypeID"
        labelAttr="legalEntityTypeName"
        required={true}
        rules={{
          required: requiredFieldMessage,
        }}
        control={control}
        errors={formState.errors}
      />
      <SelectMenu
        label={t('provider.legalEntityStatus')}
        name="LegalEntityStatusID"
        values={legalEntityStatuses}
        valueAttr="legalEntityStatusID"
        labelAttr="legalEntityStatusName"
        required={true}
        rules={{
          required: requiredFieldMessage,
        }}
        control={control}
        errors={formState.errors}
      />
    </>
  );
}
